import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useState
} from "react";
import { jwtDecode } from "jwt-decode";
import {
  ContextHolder,
  useAuth as useFronteggAuth,
  useLoginWithRedirect as useFronteggLoginWithRedirect
} from "@frontegg/react";
import { clearViewCache } from "@/services/cache/viewCache";
import { User as FronteggUser } from "@frontegg/redux-store";
import {
  ChildAccountIds,
  getActiveAccountStorageKey,
  persistAccountId,
  readPersistedAccountId,
  resolveActiveAccountId
} from "@/services/auth/activeAccount";

const LOGOUT_CHANNEL_PREFIX = "logout.auth.dreamhub--";
export const AUTH_REFRESH_INTERVAL = 60 * 1000;

// Role permissions are injected into the JWT by the Frontegg token-generation
// hook under customClaims.permissions (mirrors the users_roles table).
interface DreamhubJwtPayload {
  exp?: number;
  iss?: string;
  customClaims?: {
    permissions?: string[];
  };
}

export interface AuthContextType {
  token: string | null;
  /**
   * The account the webapp acts in — sent as X-Tenant-Id and used to scope
   * caches. Equals `homeTenantId` unless the user switched to a child account.
   */
  tenantId: string | null;
  /** The Frontegg tenant the user signed in to. */
  homeTenantId: string | null;
  /** The home account's `child_account_ids`; `undefined` until it is read. */
  childAccountIds: ChildAccountIds;
  /** The active account is a child, not the home account. */
  isLinkedAccount: boolean;
  /** A persisted account choice is waiting on the home account's child list. */
  isResolvingAccount: boolean;
  /** Pass a child id to act in it, or `null` to act in the home account. */
  setActiveAccountId: (accountId: string | null) => void;
  /**
   * `readFailed` marks an unreadable child list: the session falls back to
   * the home account but the persisted choice is kept for the next load.
   */
  reportChildAccountIds: (childAccountIds: string[], readFailed?: boolean) => void;
  login: (payload?: Record<string, string>) => void;
  logout: () => void;
  isAuthenticated: boolean;
  user: FronteggUser | null | undefined;
  tokenExpiry: number;
  permissions: string[];
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const { user, isAuthenticated } = useFronteggAuth();
  const login = useFronteggLoginWithRedirect();

  const token = useMemo(() => user?.accessToken || null, [user?.accessToken]);
  const decodedToken = useMemo(
    () => (token ? jwtDecode<DreamhubJwtPayload>(token) : null),
    [token]
  );
  const permissions = useMemo(
    () => decodedToken?.customClaims?.permissions ?? [],
    [decodedToken]
  );
  const tokenExpiry = useMemo(
    () => (decodedToken?.exp ? decodedToken.exp * 1000 : 0),
    [decodedToken?.exp]
  );
  const homeTenantId = user?.tenantId || null;
  const userId = user?.id || null;
  const storageKey =
    homeTenantId && userId
      ? getActiveAccountStorageKey(homeTenantId, userId)
      : null;

  const persistedAccountId = useMemo(
    () => (storageKey ? readPersistedAccountId(storageKey) : null),
    [storageKey]
  );
  const [requestedOverride, setRequestedOverride] = useState<{
    storageKey: string | null;
    accountId: string | null;
  } | null>(null);
  const requestedAccountId =
    requestedOverride && requestedOverride.storageKey === storageKey
      ? requestedOverride.accountId
      : persistedAccountId;

  const [reportedChildren, setReportedChildren] = useState<{
    homeTenantId: string | null;
    childAccountIds: string[];
    readFailed: boolean;
  } | null>(null);
  const childAccountIds: ChildAccountIds =
    reportedChildren && reportedChildren.homeTenantId === homeTenantId
      ? reportedChildren.childAccountIds
      : undefined;

  const tenantId = resolveActiveAccountId(
    homeTenantId,
    requestedAccountId,
    childAccountIds
  );
  const isLinkedAccount = tenantId !== null && tenantId !== homeTenantId;
  const isResolvingAccount =
    requestedAccountId !== null && childAccountIds === undefined;

  const setActiveAccountId = useCallback(
    (accountId: string | null) => {
      const requested =
        accountId && accountId !== homeTenantId ? accountId : null;
      if (storageKey) {
        persistAccountId(storageKey, requested);
      }
      setRequestedOverride({ storageKey, accountId: requested });
    },
    [storageKey, homeTenantId]
  );

  const reportChildAccountIds = useCallback(
    (reportedChildAccountIds: string[], readFailed = false) => {
      setReportedChildren({
        homeTenantId,
        childAccountIds: reportedChildAccountIds,
        readFailed
      });
    },
    [homeTenantId]
  );

  // A persisted account the home account no longer lists is dropped, so the
  // next load starts in the home account without waiting on the setting again.
  // A list that failed to load proves nothing, so the choice is kept for the
  // next load, but this session stays in the home account: a later refetch
  // must not move it into the child without the clearing a switch does.
  const childListReadFailed = reportedChildren?.readFailed ?? false;
  useEffect(() => {
    if (
      requestedAccountId === null ||
      childAccountIds === undefined ||
      tenantId !== homeTenantId
    ) {
      return;
    }
    if (childListReadFailed) {
      setRequestedOverride({ storageKey, accountId: null });
    } else {
      setActiveAccountId(null);
    }
  }, [
    requestedAccountId,
    childAccountIds,
    childListReadFailed,
    tenantId,
    homeTenantId,
    storageKey,
    setActiveAccountId
  ]);

  useEffect(() => {
    if (!decodedToken?.iss) return;

    const handleStorage = (e: StorageEvent) => {
      if (e.key === `${LOGOUT_CHANNEL_PREFIX}${decodedToken.iss}`) {
        const baseUrl = ContextHolder.getContext().baseUrl;
        window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location.protocol}//${window.location.host}`;
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [decodedToken?.iss]);

  const logout = useCallback(() => {
    clearViewCache();
    if (decodedToken?.iss) {
      localStorage.setItem(
        `${LOGOUT_CHANNEL_PREFIX}${decodedToken.iss}`,
        Date.now().toString()
      );
    }
    const baseUrl = ContextHolder.getContext().baseUrl;
    window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location.protocol}//${window.location.host}`;
  }, [decodedToken?.iss]);

  const contextValue = useMemo(
    () => ({
      token,
      tenantId,
      homeTenantId,
      childAccountIds,
      isLinkedAccount,
      isResolvingAccount,
      setActiveAccountId,
      reportChildAccountIds,
      login,
      logout,
      isAuthenticated,
      user,
      tokenExpiry,
      permissions
    }),
    [
      token,
      tenantId,
      homeTenantId,
      childAccountIds,
      isLinkedAccount,
      isResolvingAccount,
      setActiveAccountId,
      reportChildAccountIds,
      login,
      logout,
      isAuthenticated,
      user,
      tokenExpiry,
      permissions
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/** Role permission gating edits of deal/contract financial fields. */
export const FINANCIALS_WRITE_PERMISSION = "financials.write";

export const SETTINGS_ADMIN_PERMISSION = "settings.admin";

export const useHasPermission = (permission: string): boolean => {
  const { permissions } = useAuth();
  return permissions.includes(permission);
};
